import React from 'react';
import UpdateLayout, { ExternalLink } from './UpdateLayout';
import { BEHAVIOR_BUNDLE_SOURCES, DELIVERY_CONTEXT } from '../../../content/snowflakeUpdates';

const codeClass =
  'overflow-x-auto rounded-xl border border-ink-200 bg-surface-subtle p-4 text-sm leading-relaxed text-ink-800';

export default function BehaviorBundlesPost() {
  return (
    <UpdateLayout
      path="/updates/behavior-change-bundles"
      title="Behavior change bundles, explained"
      sources={BEHAVIOR_BUNDLE_SOURCES}
    >
      <p>
        A behavior change release, or BCR, is a change that can make existing code return different
        results or fail. Snowflake groups BCRs into monthly bundles named like 2026_07 or 2026_08.
        You turn a bundle on or off as a whole. You cannot pick a single change inside it (
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/intro-bcr-releases">
          overview
        </ExternalLink>
        ). {DELIVERY_CONTEXT}
      </p>

      <h2 className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        Testing, opt-out, then generally enabled
      </h2>
      <ol className="list-decimal space-y-3 pl-5">
        <li>
          <span className="font-semibold text-ink-900">Testing.</span> The bundle is disabled by
          default. You can enable it, usually on a development or QA account.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Opt-out.</span> The bundle is enabled by
          default. You can disable it to postpone the change, usually on production.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Generally enabled.</span> The bundle is locked
          on. Overrides are cleared.
        </li>
      </ol>
      <p>
        Bundle 2026_05 shows the pace. It was introduced in June, enabled by default in July, and
        generally enabled in September. Each stage is about five to six weeks. If you explicitly set
        a bundle during testing, Snowflake will not flip it for you when the opt-out stage starts (
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/bcr-bundles/managing-behavior-change-releases">
          managing bundles
        </ExternalLink>
        ).
      </p>

      <h2 className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        How to check status
      </h2>
      <p>
        These are account-admin actions. The status function returns ENABLED, DISABLED, or RELEASED.
        RELEASED means generally enabled and permanent. Disable errors once the opt-out window has
        closed (
        <ExternalLink href="https://docs.snowflake.com/en/sql-reference/functions/system_behavior_change_bundle_status">
          status function
        </ExternalLink>
        ).
      </p>
      <pre className={codeClass}>
        <code>{`SELECT SYSTEM$BEHAVIOR_CHANGE_BUNDLE_STATUS('2026_07');
SELECT SYSTEM$ENABLE_BEHAVIOR_CHANGE_BUNDLE('2026_07');
SELECT SYSTEM$DISABLE_BEHAVIOR_CHANGE_BUNDLE('2026_07');`}</code>
      </pre>

      <h2 className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        BCR-2287, in the 2026_07 bundle
      </h2>
      <p>
        BCR-2287 is the semantic-view change in bundle 2026_07. When that bundle is enabled, a query
        that asks for FACTS and DIMENSIONS together in SEMANTIC_VIEW() fails at compile time. Before
        the change, the same query returned a fact value Snowflake picked arbitrarily for each
        dimension group, so the result was not deterministic (
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/bcr-bundles/2026_07/bcr-2287">
          BCR-2287
        </ExternalLink>
        ).
      </p>
      <p>
        These shapes are unaffected: METRICS only, DIMENSIONS only, FACTS only, and DIMENSIONS with
        METRICS.
      </p>
      <p>
        The fix follows the row you want. The clause at query time decides, not only how the column
        is defined on the view. For unique rows, put the column in DIMENSIONS. For non-unique rows,
        put the column in FACTS.
      </p>
      <pre className={codeClass}>
        <code>{`-- unique rows
SELECT *
FROM SEMANTIC_VIEW(
  sv
  DIMENSIONS f1, d1
);

-- non-unique rows
SELECT *
FROM SEMANTIC_VIEW(
  sv
  FACTS f1, d1
);`}</code>
      </pre>
      <p>
        The note also includes this lookback over QUERY_HISTORY for the last 30 days:
      </p>
      <pre className={codeClass}>
        <code>{`SELECT *
FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY
WHERE start_time >= DATEADD('day', -30, CURRENT_TIMESTAMP())
  AND RLIKE(query_text,
    $$.*from\\s+semantic_view.*(facts.*dimensions|dimensions.*facts).*$$, 'is')
ORDER BY start_time DESC;`}</code>
      </pre>
      <p>
        Bundle 2026_07 is enabled by default per Snowflake&apos;s{' '}
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/2026/10_37">
          10.37 release notes
        </ExternalLink>
        . The 2026_07 bundle page still shows &quot;Disabled by default,&quot; and 10.37 is labeled
        Preview, so verify the state in your own account with{' '}
        <ExternalLink href="https://docs.snowflake.com/en/sql-reference/functions/system_show_active_behavior_change_bundles">
          SYSTEM$SHOW_ACTIVE_BEHAVIOR_CHANGE_BUNDLES
        </ExternalLink>
        .
      </p>
      <pre className={codeClass}>
        <code>SELECT SYSTEM$SHOW_ACTIVE_BEHAVIOR_CHANGE_BUNDLES();</code>
      </pre>
    </UpdateLayout>
  );
}
